import { NavLink, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import SecondaryBar from "../SecondaryBar";
import { CTA, InfoCard } from "../Generic";
// import ProtocolList from "../ProtocolList";
// import SubstanceList from "../SubstanceList";
import {
  selectDrugs,
  selectExams,
  selectTreatments,
  selectAnalysis,
  selectWineMaking,
  selectUsers,
} from "../../context/selectors";
import { getDrugs, getExams, getProtocols, getUsers } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setExams } from "../../context/substances/examsSlice";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import grape from "../../assets/icons/grape.png";
import must from "../../assets/icons/must.png";
import wine from "../../assets/icons/wine.png";
import additives from "../../assets/icons/additives.png";
import adjuvants from "../../assets/icons/adjuvants.png";
import { SkeletonTableEdit } from "../Skeleton/Skeleton";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import TabSelector from "../TabSelector";
import { getItem, setItem } from "../../services/LocalStorage";
import { setUsers } from "../../context/users/usersSlice";

const ProtocolList = lazy(() => import("../ProtocolList"));
// const SubstanceList = lazy(() => import("../SubstanceList"));

const Protocols = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const { protocolType } = useParams();
  // const availableTreatmentsName = getLabel("substanceTitlePage");
  const users = useSelector(selectUsers);
  const protocols = useSelector(
    protocolType === "treatment" ? selectTreatments : selectAnalysis
  );
  const winemakingProtocols = useSelector(selectWineMaking);
  const substances = useSelector(
    protocolType === "treatment" ? selectDrugs : selectExams
  );
  const [protocolsLen, setProtocolsLen] = useState({});
  const [substancesLen, setSubstancesLen] = useState({});
  const [activeTab, setActiveTab] = useState("treatment");
  const tabs = [
    { name: getLabel("protocolSubtype_PHYSICAL"), anchor: "physical" },
    { name: getLabel("protocolSubtype_PRODUCTS"), anchor: "treatment" },
  ];

  const handleTabChange = (anchor) => {
    setActiveTab(anchor);
    if (["treatment", "physical"].includes(protocolType)) setItem(`activeTab-treatment-protocols`, anchor);
  };

    const getLastActiveTab = () => {
      try {
        const savedTab = getItem(`activeTab-treatment-protocols`);
        if (savedTab && tabs.some(tab => tab.anchor === savedTab)) {
          setActiveTab(savedTab);
          return savedTab;
        }
        return null;
      } catch (error) {
        return null;
      }
    }

  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;

  const loadSubstances = async () => {
    const currentSubstances = await (protocolType === "treatment"
      ? getDrugs(axiosPrivate)
      : getExams(axiosPrivate));
    dispatch(
      protocolType === "treatment"
        ? setDrugs(currentSubstances)
        : setExams(currentSubstances)
    );
  };

  useEffect(() => {
    if (!substances || substances.length === 0) {
      loadSubstances();
    }
    if (["treatment", "physical"].includes(protocolType)) getLastActiveTab();
  }, [protocolType]);

  const loadProtocols = async () => {
    const currentProtocols =
      (await getProtocols(protocolType, axiosPrivate)) || [];
    dispatch(
      protocolType === "treatment"
        ? setTreatments(currentProtocols)
        : setAnalysis(currentProtocols)
    );
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  const loadWinemakingProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };
  
  useEffect(() => {
    if (!protocols || protocols.length === 0) {
      loadProtocols();
    }
    if (protocolType === 'treatment' && (!winemakingProtocols || winemakingProtocols.length === 0)) {
      loadWinemakingProtocols();
    }
    if (!users || users.length === 0) {
      loadUsers();
    }
  }, [protocolType]);

  const renderHeader = () => {
    return (
      <div className="primary_container_menu">
        <h2>{getLabel("protocols")}</h2>
      </div>
    );
  };

  useEffect(() => {
    if (!protocols || protocols?.length === 0) return;
    const tmp = {};
    tmp.tot = protocols?.length || 0;
    const wine = protocols?.filter((t) => t?.state === "WINE");
    const must = protocols?.filter((t) => t?.state === "MUST");
    const grape = protocols?.filter((t) => t?.state === "GRAPE");

    tmp.wine = wine?.length;
    tmp.must = must?.length;
    tmp.grape = grape?.length;

    tmp.wines = {
      red: wine?.filter((t) => t?.color === "RED")?.length,
      white: wine?.filter((t) => t?.color === "WHITE")?.length,
      rose: wine?.filter((t) => t?.color === "ROSE")?.length,
    };
    tmp.musts = {
      red: must?.filter((t) => t?.color === "RED")?.length,
      white: must?.filter((t) => t?.color === "WHITE")?.length,
      rose: must?.filter((t) => t?.color === "ROSE")?.length,
    };
    tmp.grapes = {
      red: grape?.filter((t) => t?.color === "RED")?.length,
      white: grape?.filter((t) => t?.color === "WHITE")?.length,
      rose: grape?.filter((t) => t?.color === "ROSE")?.length,
    };

    setProtocolsLen(tmp);
  }, [protocols]);

  useEffect(() => {
    if (!substances || substances?.length === 0) return;
    const tmp = {};
    tmp.tot = substances?.length || 0;
    const additive = substances?.filter((t) => t?.type === "ADDITIVE");
    const adjuvant = substances?.filter((t) => t?.type === "ADJUVANT");
    tmp.additive = additive?.length;
    tmp.adjuvant = adjuvant?.length;

    setSubstancesLen(tmp);
  }, [substances]);

  const getTooltipContent = (obj = {}) => {
    return (
      <ul>
        {Object.keys(obj)?.map((o, index) => (
          <li className="tooltip_label" key={`tooltip_${index}`}>{`${getLabel(
            `lotColor_${o?.toUpperCase()}`
          )}: ${obj?.[o]}`}</li>
        ))}
      </ul>
    );
  };

  const renderNavMenu = (navType = null) => {
    return (
      <div className="primary_container_menu">
        <div>
          <h5 className="tanks_title">
            {getLabel("numTotProtocols")}
            <span className="tanks_span">{navType ? (winemakingProtocols?.filter(w => w?.protocol_type === navType) || [])?.length : protocolsLen?.tot || "0"}</span>
          </h5>
        </div>
        <div 
          className={protocolType === "analysis" ? "tanks_wrapper" : "protocols_wrapper"}
        >
          {role === ROLES.CLIENT && (
            <NavLink
              className="cta_wrapper"
              to={navType === 'PHYSICAL' ? `/protocols/winemaking/${navType?.toLowerCase?.()}/new` : `/protocols/${protocolType?.toLowerCase?.()}/new`}
            >
              <CTA customClassName={"create"}>
                {protocolType === "treatment"
                  ? getLabel(navType === "PHYSICAL" ? "createWineMakingPhysicalProtocol" : "createTreatmentProtocol")
                  : getLabel("createAnalysisProtocol")}{" "}
                <FontAwesomeIcon icon="fa-plus" />
              </CTA>
            </NavLink>
          )}
          {protocolType === "analysis" && (
            <div className="tanks_recap">
              <div className="tanks_recap_wrapper">
                <InfoCard
                  label={getLabel("numTotGrapeLots")}
                  number={protocolsLen?.grape}
                  img={isMobile || true ? grape : null}
                  isGrey={protocolsLen?.grape === 0}
                  content={getTooltipContent(protocolsLen?.grapes)}
                />
                <InfoCard
                  label={getLabel("numTotMustLots")}
                  number={protocolsLen?.must}
                  img={isMobile || true ? must : null}
                  isGrey={protocolsLen?.musts === 0}
                  content={getTooltipContent(protocolsLen?.musts)}
                />
                <InfoCard
                  label={getLabel("numTotWineLots")}
                  number={protocolsLen?.wine}
                  img={isMobile || true ? wine : null}
                  isGrey={protocolsLen?.wines === 0}
                  content={getTooltipContent(protocolsLen?.wines)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDrugsNavMenu = () => {
    return (
      <div className="primary_container_menu">
        <div>
          <h5 className="tanks_title">
            {getLabel("numTotSubstances")}
            <span className="tanks_span">{substancesLen?.tot || "0"}</span>
          </h5>
        </div>
        <div
          className={`tanks_wrapper ${
            protocolType === "analysis" ? "" : "no-center"
          }`}
        >
          {role === ROLES.CLIENT && (
            <NavLink
              className="cta_wrapper"
              to={`/warehouse/${protocolType}/new`}
            >
              <CTA customClassName={"create"}>
                {getLabel("createDrug")} <FontAwesomeIcon icon="fa-plus" />
              </CTA>
            </NavLink>
          )}
          <div className="tanks_recap">
            <div className="tanks_recap_wrapper">
              <InfoCard
                label={getLabel("numTotAdjuvants")}
                number={substancesLen?.adjuvant}
                img={isMobile || true ? adjuvants : null}
              />
              <InfoCard
                label={getLabel("numTotAdditives")}
                number={substancesLen?.additive}
                img={isMobile || true ? additives : null}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[
          protocolType === "treatment"
            ? getLabel("treatmentProtocolsNavLink")
            : getLabel("analysisProtocolsNavLink"),
        ]}
        isBasePathNeeded={false}
      />
      {renderHeader()}
      {["physical", "treatment"].includes(protocolType) && <TabSelector
        tabs={tabs}
        onTabChange={handleTabChange}
        activeTab={activeTab}
      />}
      {activeTab !== 'physical' && renderNavMenu()}
      {activeTab !== 'physical' && <Suspense
        fallback={
          <SkeletonTableEdit colCount={protocolType == "analysis" ? 5 : 4} />
        }
      >
        <ProtocolList
          type={protocolType}
          protocols={protocols}
          users={users}
          isMobile={isMobile}
        />
      </Suspense>}
      {activeTab === 'physical' && protocolType === 'treatment' && renderNavMenu('PHYSICAL')}
      {activeTab === 'physical' && protocolType === 'treatment' && <Suspense
        fallback={
          <SkeletonTableEdit colCount={4} />
        }
      >
        <ProtocolList
          // type={protocolType}
          type={"physical"}
          users={users}
          protocols={winemakingProtocols?.filter(p => p?.protocol_type === 'PHYSICAL')}
          isMobile={isMobile}
        />
      </Suspense>}
    </div>
  );
};

export default Protocols;
